/**
 * BulkCorrectionsDialog — Wave 6.3 SuperAdmin metadata-correction surface.
 *
 * Field-by-field UI: AssignedOfficerCode, BranchCode, ResolutionCode,
 * ResolutionReason, retention override (datetime). OriginRole is grayed-out
 * + read-only with the helper text "OriginRole is immutable for audit
 * integrity — bulk-correct does NOT mutate it."
 *
 * Backend rejects keys not in its allowlist; this UI just keeps the
 * caller from trying.
 */

import { useEffect, useState } from 'react';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { ApiError } from '@retail-ops/api-client';
import type {
  CartResolutionCode,
  RecordsBrowseRow,
  RecordsBulkCorrectInput,
  Iso8601,
} from '@retail-ops/shared-types';
import { useConfig } from '@/contexts/ConfigContext';
import { promptBypassKey } from '@/modules/Admin/editors/_shared/CrudTable';
import { useRecordsApi } from '../hooks/useRecordsApi';

const RESOLUTIONS: ReadonlyArray<{ value: CartResolutionCode | ''; label: string }> = [
  { value: '', label: 'Leave unchanged' },
  { value: 'Open', label: 'Open' },
  { value: 'Processed', label: 'Processed' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'Adverse-Action', label: 'Adverse Action' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'System-Error', label: 'System Error' },
];

export interface BulkCorrectionsDialogProps {
  open: boolean;
  rows: ReadonlyArray<RecordsBrowseRow>;
  onClose: () => void;
  onCompleted: () => void;
}

export function BulkCorrectionsDialog({ open, rows, onClose, onCompleted }: BulkCorrectionsDialogProps): JSX.Element {
  const { client, makeSignal } = useRecordsApi();
  const { config } = useConfig();

  const [assignedOfficerCode, setAssignedOfficerCode] = useState<string>('');
  const [branchCode, setBranchCode] = useState<string>('');
  const [resolutionCode, setResolutionCode] = useState<CartResolutionCode | ''>('');
  const [resolutionReason, setResolutionReason] = useState<string>('');
  const [retentionEndsAt, setRetentionEndsAt] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAssignedOfficerCode('');
    setBranchCode('');
    setResolutionCode('');
    setResolutionReason('');
    setRetentionEndsAt('');
    setError(null);
  }, [open]);

  const hasAnyChange =
    assignedOfficerCode !== '' ||
    branchCode !== '' ||
    resolutionCode !== '' ||
    resolutionReason !== '' ||
    retentionEndsAt !== '';

  async function handleSubmit(): Promise<void> {
    if (!hasAnyChange || submitting) return;
    const bypass = promptBypassKey();
    if (!bypass) {
      setError('A bypass key is required to apply corrections.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const corrections: RecordsBulkCorrectInput['corrections'] = {};
      if (assignedOfficerCode) corrections.assignedOfficerCode = assignedOfficerCode;
      if (branchCode) corrections.branchCode = branchCode;
      if (resolutionCode) corrections.resolutionCode = resolutionCode;
      if (resolutionReason) corrections.resolutionReason = resolutionReason;
      if (retentionEndsAt) corrections.retentionEndsAt = new Date(retentionEndsAt).toISOString() as Iso8601;

      const res = await client.retail.superAdmin.records.correct(
        bypass,
        {
          records: rows.map((r) => ({ entityType: r.entityType, entityKey: r.entityKey })),
          corrections,
        },
        { signal: makeSignal() },
      );
      if (!res.success) {
        setError(res.error ?? 'Bulk correction returned success=false.');
        return;
      }
      onCompleted();
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError('Unexpected error during bulk correction.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={() => (submitting ? undefined : onClose())}
      title={`Bulk correct ${rows.length} record${rows.length === 1 ? '' : 's'}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={!hasAnyChange || submitting}>
            {submitting ? 'Applying…' : 'Apply corrections'}
          </Button>
        </>
      }
    >
      <Banner tone="info">
        Apply the same metadata correction to <strong>{rows.length}</strong> record
        {rows.length === 1 ? '' : 's'}. Leave any field as <em>"Leave unchanged"</em> to keep
        the existing value.
      </Banner>

      <div style={{ marginTop: 'var(--sp-5)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
        <Field label="Assigned officer code" help="Officer code (e.g. 425). Free-text — verified server-side against Users.">
          <input
            aria-label="Assigned officer code override"
            title="Assigned officer code override"
            type="text"
            value={assignedOfficerCode}
            onChange={(e) => setAssignedOfficerCode(e.target.value)}
            placeholder="Leave blank to skip"
            style={inputStyle}
          />
        </Field>
        <Field label="Branch">
          <select aria-label="Branch override" title="Branch override" value={branchCode} onChange={(e) => setBranchCode(e.target.value)} style={inputStyle}>
            <option value="">Leave unchanged</option>
            {config.branches.map((b) => (
              <option key={b.branchCode} value={b.branchCode}>
                {b.branchName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Resolution code">
          <select
            aria-label="Resolution code override"
            title="Resolution code override"
            value={resolutionCode}
            onChange={(e) => setResolutionCode(e.target.value as CartResolutionCode | '')}
            style={inputStyle}
          >
            {RESOLUTIONS.map((r) => (
              <option key={r.value || 'noop'} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Retention ends at">
          <input
            aria-label="Retention end override"
            title="Retention end override"
            type="datetime-local"
            value={retentionEndsAt}
            onChange={(e) => setRetentionEndsAt(e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Resolution reason" style={{ gridColumn: '1 / -1' }}>
          <input
            aria-label="Resolution reason override"
            title="Resolution reason override"
            type="text"
            value={resolutionReason}
            onChange={(e) => setResolutionReason(e.target.value)}
            placeholder="Leave unchanged"
            style={inputStyle}
          />
        </Field>
        <Field
          label="Origin role"
          style={{ gridColumn: '1 / -1' }}
          help="OriginRole is immutable for audit integrity — bulk-correct does NOT mutate it."
        >
          <input aria-label="Origin role (immutable)" title="Origin role is immutable" value="(immutable)" readOnly style={{ ...inputStyle, background: 'var(--ink-100)', color: 'var(--ink-500)' }} />
        </Field>
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: 'var(--sp-5)', fontSize: 12, color: 'var(--ink-600)' }}>
          Applies to:{' '}
          {rows.slice(0, 5).map((r, i) => (
            <span key={`${r.entityType}::${r.entityKey}`} style={{ marginRight: 8 }}>
              <Pill variant="muted">{r.entityType}</Pill> {r.displayLabel}
              {i < Math.min(5, rows.length) - 1 && ','}
            </span>
          ))}
          {rows.length > 5 && <span> and {rows.length - 5} more</span>}
        </div>
      )}

      {error && (
        <Banner tone="err" title="Correction failed">
          {error}
        </Banner>
      )}
    </ModalShell>
  );
}

function Field({
  label,
  help,
  children,
  style,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--ink-700)', ...style }}>
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-500)', fontSize: 11 }}>
        {label}
      </span>
      {children}
      {help && <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>{help}</span>}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  fontSize: 13,
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  background: 'var(--surface-card)',
  width: '100%',
};
